// @givmo/app-admin — employer BULK gift-card Idempotency-Key signature (EP-WEB5). ★ MONEY-MOVING ★
//
// Sending a company gift-card BATCH makes ONE money movement — one COMPANY_CARD charge (or one
// COMPANY_WALLET withdraw) for the batch total — via `POST /admin/company/gift-cards/batches`, guarded
// by the backend `claim_idempotency` dependency + the batch-row `purchase_request_id` unique index.
// De-dup happens ONLY across retries carrying the SAME `Idempotency-Key` + batch `purchase_request_id`;
// an unkeyed retry (or a fresh key on retry) mints a NEW batch and does NOT cross-retry-dedup, so a
// double-click / network retry would DOUBLE-CHARGE the whole batch. A stable client key is the only
// thing preventing that. Like the single-send, this key rides BOTH the `Idempotency-Key` header AND the
// body `purchase_request_id` (see `buildCompanyGiftCardBatchBody`).
//
// Series identity for a batch = the exact RECIPIENT SET (work emails, order-independent) + the uniform
// whole-dollar denomination + the funding source (a specific card id, or the wallet). Adding/removing a
// recipient, changing the denomination, or switching funding is a genuinely different batch → a fresh
// key (re-mint on edit); merely REORDERING the same recipients is the SAME batch → the SAME key. The
// generic lifecycle holder is shared from `@givmo/payments`; this file supplies only the batch-specific
// signature + a bound factory.

import { IdempotencyKeyHolder } from '@givmo/payments';
import { inCanonicalRecipientOrder, recipientDedupeKey } from './gift-cards-batch-decode';
import type { Funding } from './gift-cards-decode';

/** A stable signature of the batch being sent; a change ends the current attempt-series. */
export interface GiftCardBatchSignature {
  /** The recipient roster employees' work emails (order-INDEPENDENT — sorted in the fingerprint). */
  readonly recipientWorkEmails: readonly string[];
  /** The uniform whole-dollar integer denomination for every card in the batch. */
  readonly denomination: number;
  /** The funding source — a specific card id, or the wallet (encodes the `card_id` axis). */
  readonly funding: Funding;
}

/**
 * Deterministic fingerprint of a batch: the SORTED (case-insensitive) recipient set + denomination +
 * funding instrument (incl. card id). Sorting makes it order-independent — the same people at the same
 * denomination on the same card is ONE batch regardless of selection order, so a reorder never re-mints
 * the key (and thus never risks a second charge on retry). The order is the one the send body uses too
 * ({@link inCanonicalRecipientOrder}, GC-FU13), so the retry it keys sends the same bytes.
 */
export function giftCardBatchFingerprint(signature: GiftCardBatchSignature): string {
  const emails = inCanonicalRecipientOrder(signature.recipientWorkEmails, (email) => email)
    .map(recipientDedupeKey)
    .join(',');
  const fundingTag =
    signature.funding.kind === 'wallet' ? 'wallet' : `card:${signature.funding.cardId}`;
  return `emails:[${emails}]|denomination:${signature.denomination}|funding:${fundingTag}`;
}

/**
 * Construct the batch idempotency-key holder, bound to {@link giftCardBatchFingerprint}. A React
 * component holds ONE holder for its lifetime (a page `useRef`), so the key is stable across re-renders
 * by construction and re-mints only when the signature changes. `generate` is injectable for tests. The
 * minted value (a UUIDv4) satisfies the backend `purchase_request_id` min-length-8 constraint.
 */
export function createGiftCardBatchIdempotencyKeyHolder(
  generate?: () => string,
): IdempotencyKeyHolder<GiftCardBatchSignature> {
  return new IdempotencyKeyHolder<GiftCardBatchSignature>(giftCardBatchFingerprint, generate);
}
